import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";
import {
    WHISPER_MIC_BACKDROP_FILTER,
    WHISPER_MIC_BG_DARK,
    WHISPER_MIC_BG_LIGHT,
    WHISPER_MIC_BORDER_DARK,
    WHISPER_MIC_BORDER_LIGHT,
    WHISPER_MIC_BUTTON_DIAMETER_PX,
    WHISPER_MIC_ICON_SIZE_PX,
    SOLID_MIC_SVG_PATH_D,
    WHISPER_MIC_FIRST_HINT_MARGIN_BOTTOM_REM,
    WHISPER_MIC_SHADOW_DARK,
    WHISPER_MIC_SHADOW_LIGHT,
    WHISPER_MIC_WRAPPER_BOTTOM_REM,
    WHISPER_MIC_Z_INDEX,
} from "../../../constants/tabBarFabWhisper";

interface MicButtonProps {
    conversationActive: boolean;
    isConnecting: boolean;
    isReconnecting: boolean;
    status: string; // 'recognizing' | 'idle' | etc
    hasCompletedFirstConversation: boolean;
    onClick: () => void;
}

export function MicButton({
    conversationActive,
    isConnecting,
    isReconnecting,
    status,
    hasCompletedFirstConversation,
    onClick
}: MicButtonProps) {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';

    return (
        <div
            className="fixed left-1/2 flex -translate-x-1/2 flex-col items-center"
            style={{
                bottom: `${WHISPER_MIC_WRAPPER_BOTTOM_REM}rem`,
                zIndex: WHISPER_MIC_Z_INDEX,
            }}
        >
            {/* First Time Hint */}
            {!hasCompletedFirstConversation && !conversationActive && (
                <div
                    className="text-center px-4"
                    style={{ marginBottom: `${WHISPER_MIC_FIRST_HINT_MARGIN_BOTTOM_REM}rem` }}
                >
                    <p className={`text-base font-medium whitespace-pre-line leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-700'
                        }`}>
                        {t('whisper.tapToStartConversation')}
                    </p>
                </div>
            )}

            <button
                onClick={onClick}
                disabled={isConnecting || isReconnecting}
                aria-label={conversationActive ? t('whisper.stop') : t('whisper.start')}
                className={`rounded-full select-none flex items-center justify-center transition-transform ${isConnecting || isReconnecting
                        ? 'cursor-not-allowed'
                        : 'hover:scale-105 active:scale-95'
                    }`}
                style={{
                    width: WHISPER_MIC_BUTTON_DIAMETER_PX,
                    height: WHISPER_MIC_BUTTON_DIAMETER_PX,
                    background: isDark ? WHISPER_MIC_BG_DARK : WHISPER_MIC_BG_LIGHT,
                    backdropFilter: WHISPER_MIC_BACKDROP_FILTER,
                    border: isDark ? WHISPER_MIC_BORDER_DARK : WHISPER_MIC_BORDER_LIGHT,
                    boxShadow: isDark ? WHISPER_MIC_SHADOW_DARK : WHISPER_MIC_SHADOW_LIGHT,
                    zIndex: WHISPER_MIC_Z_INDEX,
                }}
            >
                {conversationActive && !isConnecting && !isReconnecting && (
                    <RippleVisualizer mode={status === "recognizing" ? 'inward' : 'outward'} isDark={isDark} />
                )}
                {isConnecting || isReconnecting ? (
                    <Loader2
                        className={`shrink-0 animate-spin ${isDark ? 'text-white' : 'text-gray-800'}`}
                        style={{ width: WHISPER_MIC_ICON_SIZE_PX, height: WHISPER_MIC_ICON_SIZE_PX }}
                    />
                ) : conversationActive ? (
                    <svg
                        className={`shrink-0 ${isDark ? 'text-white' : 'text-gray-800'}`}
                        style={{ width: WHISPER_MIC_ICON_SIZE_PX, height: WHISPER_MIC_ICON_SIZE_PX }}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                ) : (
                    <svg
                        className={`shrink-0 ${isDark ? 'text-white' : 'text-gray-800'}`}
                        style={{ width: WHISPER_MIC_ICON_SIZE_PX, height: WHISPER_MIC_ICON_SIZE_PX }}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                    >
                        <path d={SOLID_MIC_SVG_PATH_D} />
                    </svg>
                )}
            </button>
        </div>
    );
}

function RippleVisualizer({ mode, isDark }: { mode: 'inward' | 'outward' | 'none'; isDark: boolean }) {
    if (mode === 'none') return null;
    const outward = mode === 'outward';
    return (
        <div className="absolute inset-0 flex items-center justify-center">
            {[0, 1, 2].map((i) => (
                <motion.div
                    key={i}
                    className={`absolute rounded-full border-2 ${isDark ? 'border-white/30' : 'border-gray-400/40'}`}
                    style={{ width: 220, height: 220 }}
                    initial={{ scale: outward ? 0.55 : 1.0, opacity: 0.4 }}
                    animate={{
                        scale: outward ? [0.55, 1.0] : [1.0, 0.55],
                        opacity: [0.4, 0.0]
                    }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: i * 0.3 }}
                />
            ))}
        </div>
    );
}
